import {
  resetCommonUIStore,
  resetGameStore,
  resetGameWindowStore,
  resetGeolocationStore,
  setClientStatus,
  setGameStatus,
  setIsJackpotModeSuppressed,
} from '@/store/actions';
import {
  clearSession,
  CLEAR_SESSION_REASON,
} from '@/unified-modules/account/account-library';
import {
  clearVitalsStorage,
  resetFullLaunchVitalsForSoftReload,
  resetGameClientIframeDomReadyForReload,
  resetLaunchAttemptVitalsForSoftReload,
  restartSoftReloadVitals,
  type VitalsErrorContext,
} from '@/utilities/logger/datadog/vitals';

import reloadIframe from './reloadIframe';
import { restartVitalsForSoftReloadPreset } from './softReloadVitals';
import { GameStoreParts } from '@/store/slices/game/types';
import { invalidateGlsLaunch } from '@/game-client/gls-launch-guard';
import type { ResetAllGamePartsOptions } from '@/store/slices/game/types';

/**
 * Named strategies for what to reset before reloading the game client iframe.
 * Use {@link applySoftReloadPreset} so reload behavior stays in one place.
 * Store resets bump each slice's `*ResetRevision` where applicable.
 */
export type SoftReloadPreset =
  | 'iframe-only'
  | 'game-client-retry'
  | 'launch-new-game'
  | 'geolocation-retry'
  | 'logout'
  | 'jackpot-retry';

export const SOFT_RELOAD_PRESET = {
  IFRAME_ONLY: 'iframe-only',
  GAME_CLIENT_RETRY: 'game-client-retry',
  LAUNCH_NEW_GAME: 'launch-new-game',
  GEOLOCATION_RETRY: 'geolocation-retry',
  LOGOUT: 'logout',
  JACKPOT_RETRY: 'jackpot-retry',
} as const satisfies Record<string, SoftReloadPreset>;

const preservesLaunchEnvelopeOnSoftReload = (
  preset: SoftReloadPreset,
): boolean => preset === 'game-client-retry' || preset === 'geolocation-retry';

/** Full vitals wipe + relaunch (Try again / jackpot reconcile), not attempt-only reset. */
const usesFullLaunchVitalsRestart = (preset: SoftReloadPreset): boolean =>
  preset === 'iframe-only' || preset === 'jackpot-retry';

const shouldPreserveLaunchVitalsForPreset = (
  preset: SoftReloadPreset,
  preserveLaunchVitals: boolean,
): boolean =>
  preserveLaunchVitals &&
  (preset === 'jackpot-retry' || preset === 'iframe-only');

/** Gap between tearing down common UI and full game reset so loading overlays do not stack. */
const LAUNCH_NEW_GAME_UI_SETTLE_MS = 20;

const delayMs = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const resetFullGameStore = (options?: ResetAllGamePartsOptions) => {
  resetGameStore(undefined, options);
  resetGameWindowStore();
};

/** Logout + geolocation retry: clear load flags but keep game context / error slices intact. */
const resetGameLoadStatusOnly = () => {
  resetGameStore(GameStoreParts.gameStatus, { skipRevisionBump: true });
  resetGameStore(GameStoreParts.clientStatus);
};

/**
 * Same-game silent relaunch (e.g. jackpot GLS reconcile): allow a new GLS fetch
 * without resetting load progress or bumping gameResetRevision (avoids loading bar remount).
 */
const resetGameLoadStatusForSilentRelaunch = (
  shouldResetLoadingScreen: boolean,
) => {
  setGameStatus({
    gameFetchStarted: false,
    gameFetchSuccess: false,
    gameFetchFailed: false,
    gameFetchErrorCode: null,
    gameLoadStarted: false,
    gameLoadSuccess: false,
    jackpotGlsPrewarmContextReceived: false,
    ...(shouldResetLoadingScreen
      ? { gameLoadProgress: 0, internalLoadProgress: 0 }
      : {}),
  });
  setClientStatus({ loaded: false });
};

/**
 * Applies store resets for the preset, then reloads the iframe once.
 */
export const applySoftReloadPreset = async (
  preset: SoftReloadPreset,
  forceIframeReload = false,
  preserveLaunchVitals = false,
  shouldResetLoadingScreen = false,
  abortLaunchVitalsError?: VitalsErrorContext,
): Promise<void> => {
  if (shouldPreserveLaunchVitalsForPreset(preset, preserveLaunchVitals)) {
    // Jackpot auto-reconcile / explicit preserve: keep the launch envelope open.
  } else if (usesFullLaunchVitalsRestart(preset)) {
    resetFullLaunchVitalsForSoftReload(
      preset === 'iframe-only'
        ? { errorType: 'try_again' }
        : (abortLaunchVitalsError ?? { errorType: 'jackpot_reconcile' }),
    );
  } else if (preservesLaunchEnvelopeOnSoftReload(preset)) {
    resetLaunchAttemptVitalsForSoftReload();
  } else {
    clearVitalsStorage();
  }
  switch (preset) {
    case 'iframe-only':
      invalidateGlsLaunch();
      resetFullGameStore();
      break;
    case 'logout':
      invalidateGlsLaunch();
      await clearSession(CLEAR_SESSION_REASON.OTHER);
      break;
    case 'game-client-retry':
      invalidateGlsLaunch();
      resetGeolocationStore();
      resetFullGameStore();
      break;
    case 'geolocation-retry':
      invalidateGlsLaunch();
      resetGeolocationStore();
      resetGameLoadStatusOnly();
      break;
    case 'launch-new-game':
      invalidateGlsLaunch();
      setIsJackpotModeSuppressed(false);
      resetCommonUIStore();
      resetGeolocationStore();
      // UI-only pause (not a deferred reset): full game reset + iframe reload stay sync.
      await delayMs(LAUNCH_NEW_GAME_UI_SETTLE_MS);
      // Full reset after invalidateGlsLaunch; GC re-handshakes on iframe reload
      // (markClientHandshakeReceived) before GL/INITIALIZE_URL delivery.
      resetFullGameStore();
      break;
    case 'jackpot-retry':
      invalidateGlsLaunch();
      resetGameLoadStatusForSilentRelaunch(shouldResetLoadingScreen);

      resetGameStore(GameStoreParts.gameError, {
        skipRevisionBump: !shouldResetLoadingScreen,
      });
      resetGameWindowStore();
      break;
    default:
      break;
  }

  if (preset !== 'logout' && !preserveLaunchVitals) {
    if (preservesLaunchEnvelopeOnSoftReload(preset)) {
      restartSoftReloadVitals(['fdLaunch', 'gameClient', 'gameLaunch']);
    } else {
      restartVitalsForSoftReloadPreset(preset, {
        fullLaunchRestart: usesFullLaunchVitalsRestart(preset),
      });
    }
  }

  resetGameClientIframeDomReadyForReload();

  // Logout: UM subscribe → handleInvalidSession → clearSessionAndRedirectToLogin
  // → resetSessionConsumers(reload). Preset soft reload here races ahead of that
  // reset (GC handshake sets loaded before clientStatus is cleared again).
  if (preset === 'logout') {
    if (forceIframeReload) {
      reloadIframe(true);
    }
    return;
  }

  reloadIframe(forceIframeReload);
};
